import { SqlForeignKey, SqlTableDefinition } from "@cervval.js/common-sql-core/src/sql.common";
import { getLogConsole, getLogService } from "@cervval.js/common/src/services/log.service";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { addToSetMap, filterNotDefined } from "@cervval.js/common/src/tools/collection.tools";
import { ObjectTools, anyReallyAny } from "@cervval.js/common/tools";
import { Action, ActionPersistResult, OperationType } from "./action.model";
import { ActionCacheService } from "./services/cache.service";
import { ID } from "./types";

/**
 * Default ID that can be used when creating new objects (before a tmp id is set)
 */
export const INIT_ID = Number.MIN_SAFE_INTEGER;

/** Cloning mode for {@link fillCacheForDetachedAction} */
export enum CloningMode {
    /** in this mode, objects keeps their ids, the detached action will work on copies,
     * but when submitting it will modify original objects
     */
    KEEP_IDS,
    /** in this mode, objects will be cloned, and a create operation will give them new ids.
     * Submitting the detached action will persist new copies of the original objects
     */
    DUPLICATE
}
/**
 * Fills the cacheManager of action, from initial objects and their children found in originalCache.
 * Depending on mode, the result of the action, when submitted, will perform operations on initial objects or on new copies.
 * @param action detached action that will work on objects clones
 * @param mode wether to duplicate objects or to modify them
 * @param objects initial objects to work on
 * @param typesToClone types to search for foreign keys and associations
 * @param originalCache initial cache of dtos to search
 * @param tableDefinitionsMap tableDefinitions to know foreign keys and associations, indexed by tableName
 */
export function fillCacheForDetachedAction<ElementTypes, FieldTypeName, AppLogInfos, Context>(
    action: Action<ElementTypes, AppLogInfos, Context>,
    mode: CloningMode,
    objects: Map<keyof ElementTypes, Set<ID>>,
    typesToClone: (keyof ElementTypes)[],
    originalCache: ActionCacheService<ElementTypes>,
    tableDefinitionsMap: Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, FieldTypeName>>
): Map<keyof ElementTypes, Record<ID, ID>> {
    let normalExit = false;
    for (let i = 0; i < 1000; i++) { // limit number of iterations
        if (!findChildren(objects, typesToClone, originalCache, tableDefinitionsMap)) {
            normalExit = true;
            break;
        }
    }
    if (!normalExit) {
        getLogService().error("Max iterations reached in fillCacheForDetachedAction");
    }

    const clonedIds: Map<keyof ElementTypes, Record<ID, ID>> = new Map(Array.from(objects.keys()).map(k => [k, {}]));
    for (const [tableName, ids] of objects.entries()) {
        for (const id of ids) {
            const object = originalCache.getDTOCacheRepository(tableName).findDTOById(id);
            if (object) {
                clone<ElementTypes, FieldTypeName, AppLogInfos, Context>(object, mode, action, tableName, originalCache, clonedIds, tableDefinitionsMap);
            }
        }
    }

    for (const [tableName, ids] of objects.entries()) {
        const tableDef = tableDefinitionsMap.get(tableName);
        if (tableDef) {
            for (const c of tableDef.columns ?? []) {
                if (c.foreignKey) {
                    updateForeignKeys<ElementTypes, AppLogInfos, Context>(ids, action, tableName, c.name as keyof ElementTypes[keyof ElementTypes], c.foreignKey, clonedIds);
                }
                if (c.isAssociativeColumn) {
                    const association = tableDef.associations?.[c.name];
                    if (association) {
                        const fkColumns = association.columns.filter(c => c.foreignKey);
                        if (fkColumns.length === 0) {
                            continue;
                        } else if (fkColumns.length !== 1) {
                            getLogService().error(`cloning objects for detached action doesn't support multiple columns with foreign keys for association, check table definitions for ${String(tableName)}, association ${c.name}`);
                            continue;
                        } else {
                            updateForeignKeys<ElementTypes, AppLogInfos, Context>(ids, action, tableName, c.name as keyof ElementTypes[keyof ElementTypes], fkColumns[0].foreignKey! as unknown as SqlForeignKey<keyof ElementTypes>, clonedIds);
                        }
                    }
                }
            }
        }
    }

    return clonedIds;
}

/** Find in originalCache every children of objects in typesToClone table list, using tableDefinitionsMap to 
 * find foreign keys and follow associations */
function findChildren<ElementTypes, FieldTypeName>(
    objects: Map<keyof ElementTypes, Set<ID>>,
    typesToClone: (keyof ElementTypes)[],
    originalCache: ActionCacheService<ElementTypes>,
    tableDefinitionsMap: Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, FieldTypeName>>
): boolean {
    let added = false;
    for (const [parentTableName, parentIds] of objects.entries()) {
        if (parentIds.size === 0) { continue; }
        for (const childTableDef of tableDefinitionsMap.values()) {
            if (typesToClone.includes(childTableDef.name)) {
                for (const c of childTableDef.columns ?? []) {
                    if (c.foreignKey == null) { continue; }
                    // ! table is a ItemValueOrFunction but in current implementation item is only passed to getItemVOFValue in updateForeignKeys
                    const cForeignKeyTable = ObjectTools.getItemVOFValue<null, keyof ElementTypes>(null, c.foreignKey.table as unknown as keyof ElementTypes, { continueWithNullItem: true });
                    if (cForeignKeyTable === parentTableName) {
                        const cache = originalCache.getDTOCacheRepository(childTableDef.name);
                        const childrenIds = cache.dtoList.filter(dto => dto && parentIds.has(getProp(dto, c.name))).map(dto => cache.getId(dto));
                        if (childrenIds.length) {
                            const ids = objects.get(childTableDef.name);
                            let previousSize = 0;
                            if (ids) {
                                previousSize = ids.size;
                                childrenIds.forEach(ids.add, ids);
                                added = previousSize !== ids.size;
                            } else {
                                objects.set(childTableDef.name, new Set(childrenIds));
                                added = true;
                            }
                        }
                    }
                }
                if (childTableDef.name === parentTableName) {
                    for (const ass in childTableDef.associations) {
                        for (const c of childTableDef.associations[ass].columns) {
                            if (c.foreignKey) {
                                // ! table is a ItemValueOrFunction but in current implementation item is only passed to getItemVOFValue in updateForeignKeys
                                const refTableNameString = ObjectTools.getItemVOFValue<null, keyof ElementTypes>(null, c.foreignKey.table as unknown as keyof ElementTypes, { continueWithNullItem: true });
                                if (refTableNameString && typesToClone.includes(refTableNameString)) {
                                    const parentCache = originalCache.getDTOCacheRepository(parentTableName);
                                    const childrenIds = filterNotDefined([...parentIds].flatMap(id => {
                                        const obj = parentCache?.findDTOById(id);
                                        return obj ? getProp<number[]>(obj, ass) : undefined;
                                    }));
                                    if (childrenIds.length) {
                                        const ids = objects.get(refTableNameString);
                                        let previousSize = 0;
                                        if (ids) {
                                            previousSize = ids.size;
                                            childrenIds.forEach(ids.add, ids);
                                            added = previousSize !== ids.size;
                                        } else {
                                            objects.set(refTableNameString, new Set(childrenIds));
                                            added = true;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    return added;
}

/** Clones object, and in DUPLICATE mode performs a create action */
function clone<ElementTypes, FieldTypeName, AppLogInfos, Context>(
    object: ElementTypes[keyof ElementTypes],
    mode: CloningMode,
    action: Action<ElementTypes, AppLogInfos, Context>,
    tableName: keyof ElementTypes,
    originalCache: ActionCacheService<ElementTypes>,
    clonedIds: Map<keyof ElementTypes, Record<ID, ID>>,
    tableDefinitionsMap: Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, FieldTypeName>>): void {

    const oldId = originalCache.getDTOCacheRepository(tableName).getId(object);
    // should always have tableName
    const clonedIdsForTableName = clonedIds.get(tableName);
    if (!clonedIdsForTableName || clonedIdsForTableName[oldId]) {
        // already cloned
        return;
    }
    const newObject = ObjectTools.clone(object);
    if (mode === CloningMode.KEEP_IDS) {
        action.getCacheManager().getDTOCacheRepository(tableName).push(newObject);
    } else if (newObject != null) {
        // empty association arrays
        const t = tableDefinitionsMap.get(tableName);
        const associativeArrays: { [columnName: string]: anyReallyAny } = {};
        if (t) {
            for (const column of t.columns) {
                if (column.isAssociativeColumn) {
                    associativeArrays[column.name] = getProp(newObject, column.name);
                    setProp(newObject, column.name, []);
                }
            }
        }
        action.create(tableName, newObject);
        // put back association arrays
        for (const columnName in associativeArrays) {
            setProp(newObject, columnName, associativeArrays[columnName]);
        }
    }
    clonedIdsForTableName[oldId] = action.getCacheManager().getDTOCacheRepository(tableName).getId(newObject);
}

/** Updates foreign keys with new ids */
function updateForeignKeys<ElementTypes, AppLogInfos, Context>(
    ids: Set<number>,
    action: Action<ElementTypes, AppLogInfos, Context>,
    tableName: keyof ElementTypes,
    propertyName: keyof ElementTypes[keyof ElementTypes],
    foreignKey: SqlForeignKey<keyof ElementTypes>,
    clonedIds: Map<keyof ElementTypes, Record<number, number>>
): void {
    for (const id of ids) {
        const updatedId = clonedIds.get(tableName)?.[id] ?? id;
        const object = action.getCacheManager().getDTOCacheRepository(tableName).getDTOById(updatedId);
        const foreignKeyTable: (keyof ElementTypes) | undefined = ObjectTools.getItemVOFValue(object, foreignKey.table);
        if (foreignKeyTable) {
            const oldIds = object[propertyName];
            if (Array.isArray(oldIds)) {
                const clonedOldIds = ObjectTools.cloneArray(oldIds);
                oldIds.length = 0; // empty array
                for (const oldId of clonedOldIds) {
                    const newId = clonedIds.get(foreignKeyTable)?.[oldId as unknown as ID];
                    if (newId) {
                        action.addToArray(tableName, object, propertyName, { addReference: { id: newId } });
                    }
                }
            } else {
                const newId = clonedIds.get(foreignKeyTable)?.[oldIds as unknown as ID];
                if (newId) {
                    action.updateValueIfNeeded(tableName, object, propertyName, newId as unknown as ElementTypes[keyof ElementTypes][keyof ElementTypes[keyof ElementTypes]]);
                }
            }
        }
    }
}

/**
 * Remove invalid operations from action
 * - creation and deletion of an item created within the action
 * - update of a deleted item (if options.removeUpdatesOnDeletedItems is true)
 * @param action action to clean
 * @param options
 * @param options.removeUpdatesOnDeletedItems Will remove update operations (and add/remove) on an item that will be deleted. This is not a good idea if you need undo/redo because cache will be wrong, and undo will be wrong.
 */
export function removeInvalidOperations<ElementTypes, AppLogInfos, Context>(action: Action<ElementTypes, AppLogInfos, Context>, options?: { removeUpdatesOnDeletedItems?: boolean }): void {

    // identify deleted items
    const deletedItemsByTableName = new Map<keyof ElementTypes, Set<ID>>();
    for (const op of action.operations) {
        if (op.getOperationType() === OperationType.DELETE) {
            const element = op.getElement();
            const id = action.getCacheManager().getDTOCacheRepository(op.elementTypeName).getId(element as ElementTypes[keyof ElementTypes]);
            addToSetMap(deletedItemsByTableName, op.elementTypeName, id);
        }
    }

    // will contain all elements that were created and deleted within the action
    const deletionsToIgnoreByTableName = new Map<keyof ElementTypes, Set<ID>>();

    // filter invalid operations
    action.operations = action.operations.filter((op) => {
        if (op.getOperationType() !== OperationType.DELETE) {
            const tableDeletedItems = deletedItemsByTableName.get(op.elementTypeName);
            if (tableDeletedItems) {
                const element = op.getElement();
                const id = action.getCacheManager().getDTOCacheRepository(op.elementTypeName).getId(element as ElementTypes[keyof ElementTypes]);
                if (tableDeletedItems.has(id)) {
                    // element will be deleted within action
                    if (op.getOperationType() === OperationType.CREATE) {
                        // element was created and deleted within the action, the deletion operation will be processed later and needs to be filtered
                        addToSetMap(deletionsToIgnoreByTableName, op.elementTypeName, id);
                        return false;
                    } else if (op.getOperationType() === OperationType.REMOVE) {
                        const tableDeletionsToIgnore = deletionsToIgnoreByTableName.get(op.elementTypeName);
                        if (tableDeletionsToIgnore?.has(id)) {
                            // element was created and deleted within the action, we also filter the REMOVE operation
                            return false;
                        } else {
                            // element will be deleted but was not created within this action => we keep the REMOVE operation (in order to remove foreign keys on deleted element)
                            return true;
                        }
                    }
                    if (options?.removeUpdatesOnDeletedItems) {
                        // UPDATE or ADD operation is invalid since element will be deleted
                        return false;
                    } else {
                        return true;
                    }
                }
            }
        }
        if (op.getOperationType() === OperationType.DELETE) {
            const tableDeletionsToIgnore = deletionsToIgnoreByTableName.get(op.elementTypeName);
            if (tableDeletionsToIgnore) {
                const element = op.getElement();
                const id = action.getCacheManager().getDTOCacheRepository(op.elementTypeName).getId(element as ElementTypes[keyof ElementTypes]);
                if (tableDeletionsToIgnore.has(id)) {
                    // element was created and deleted within the action => we filter this deleted operation
                    return false;
                }
            }
        }
        // operation is valid 
        return true;
    });

}

/**
 * @returns the mapped updatedIds in result or id if there is no mapping
 */
export function getUpToDateId(elementTypeName: string, id: number, result: ActionPersistResult): number {
    if (result.updatedIds[elementTypeName]) {
        const newId = result.updatedIds[elementTypeName][id];
        if (newId && newId !== id) {
            return getUpToDateId(elementTypeName, newId, result);
        }
    }
    return id;
}

/**
 * Update foreign keys of all elements and register updated ids in result
 */
export function updateForeignIds<DTO, ElementTypeName>(p: {
    elements: DTO[],
    elementTypeName: string,
    idColumnName: string,
    foreignKeys: { [columName: string]: SqlForeignKey<ElementTypeName> },
    result: ActionPersistResult,
    /** if updateElements is false, result is filled but element is not update (this is useful for unit tests where we want to get updated ids without affecting elements in memory) */
    updateElements: boolean
}): void {
    // foreign keys by columnName map 
    const { elements, elementTypeName, idColumnName, foreignKeys, result, updateElements } = p;

    if (foreignKeys) {
        for (const foreignFieldName in foreignKeys) {
            for (const element of elements) {
                if (element == null) { continue; }
                if (getProp(element, foreignFieldName)) {// BGS : we need to update the ids even if they are positive because an object can be removed and recreated with a new id later (cancel)
                    const foreignKeyTable = ObjectTools.getItemVOFValue(element, foreignKeys[foreignFieldName].table);

                    if (foreignKeyTable && Array.isArray(getProp(element, foreignFieldName))) {
                        const oldForeignIds: number[] = getProp(element, foreignFieldName);
                        for (let i = 0; i < oldForeignIds.length; i++) {
                            const oldForeignId = oldForeignIds[i];
                            const updatedForeignId = getUpToDateId(String(foreignKeyTable), oldForeignId, result);
                            if (updatedForeignId < 0) {
                                getLogConsole().error("Could not find up to date id for " + foreignKeyTable + "." + oldForeignId + " on " + elementTypeName + "." + getProp(element, idColumnName));
                            } else if (updatedForeignId !== oldForeignId) {
                                if (!result.updatedForeignKeys) { result.updatedForeignKeys = []; }
                                if (updateElements) {
                                    setProp(element, foreignFieldName[i], updatedForeignId);
                                }
                                result.updatedForeignKeys.push({
                                    elementId: getProp(element, idColumnName),// BGS : can be negative :  it is handled client side
                                    elementTypeName: elementTypeName,
                                    field: foreignFieldName,
                                    newId: updatedForeignId,
                                    oldId: oldForeignId
                                });
                            }
                        }
                    } else if (foreignKeyTable && element != null) {
                        const oldForeignId: number = getProp(element, foreignFieldName);
                        const updatedForeignId = getUpToDateId(String(foreignKeyTable), oldForeignId, result);
                        if (oldForeignId !== updatedForeignId) {
                            if (updatedForeignId < 0) {
                                getLogConsole().error("Could not find up to date id for " + foreignKeyTable + "." + oldForeignId + " on " + elementTypeName + "." + getProp(element, idColumnName));
                                if (updateElements) {
                                    setProp(element, foreignFieldName, oldForeignId);
                                }
                            } else {
                                if (updateElements) {
                                    setProp(element, foreignFieldName, updatedForeignId);
                                }
                                if (!result.updatedForeignKeys) { result.updatedForeignKeys = []; }
                                result.updatedForeignKeys.push({
                                    elementId: getProp(element, idColumnName),// BGS : can be negative :  it is handled client side
                                    elementTypeName: elementTypeName,
                                    field: foreignFieldName,
                                    newId: updatedForeignId,
                                    oldId: oldForeignId
                                });
                            }
                        }
                    } else {
                        getLogConsole().error(elementTypeName, "Invalid foreignFieldName", foreignFieldName);
                    }
                }
            }
        }
    }
}